/**
 * Resolução do tenant.
 *
 * O suffix identifica a base de dados do cliente no backend. Ele NÃO é
 * configuração do build nem do deploy: é DADO DA REQUISIÇÃO. O Kong resolve o
 * tenant pelo Host e injeta `x-client-suffix` antes de encaminhar — removendo
 * antes qualquer valor que o cliente tenha mandado, para não dar pra forjar.
 *
 * É isso que permite UM único Deployment atender todos os clientes: dois hosts
 * diferentes chegam no mesmo pod e saem com suffixes diferentes.
 *
 * O browser nunca precisa saber o suffix — ele fala só com o proxy same-origin
 * /api/nest, e é o proxy que carimba o header ao chamar a nest. Nada de tenant
 * é assado no bundle.
 *
 * Este módulo é client-safe de propósito (sem `server-only`): o `http` é
 * isomórfico e o importa. O acesso a `next/headers` é feito por import
 * dinâmico, dentro do ramo que só roda no servidor.
 */

export const CLIENT_SUFFIX_HEADER = "x-client-suffix";

/**
 * Erro de tenant ausente. Distinguível para quem quiser tratar de forma
 * específica (ex.: devolver 400 em vez de 500 num route handler).
 */
export class MissingTenantError extends Error {
    constructor() {
        super(
            `Requisição sem ${CLIENT_SUFFIX_HEADER}. Em produção este header é ` +
                `injetado pelo Kong por host — se ele não chegou, a requisição não ` +
                `passou pelo Ingress de nenhum tenant. Em desenvolvimento, defina ` +
                `CLIENT_SUFFIX no .env.`
        );
        this.name = "MissingTenantError";
    }
}

/**
 * Núcleo puro da resolução, reusado por quem já tem o header em mãos.
 *
 * Fora de produção aceita CLIENT_SUFFIX do ambiente, para `next dev` rodar sem
 * um Kong na frente. Em produção NÃO há fallback: servir um tenant padrão seria
 * pior que falhar — entregaria os dados do cliente errado em silêncio.
 */
export function resolveClientSuffix(headerValue: string | null | undefined): string {
    const fromHeader = headerValue?.trim();
    if (fromHeader) return fromHeader;

    if (process.env.NODE_ENV !== "production") {
        const fromEnv = process.env.CLIENT_SUFFIX?.trim();
        if (fromEnv) return fromEnv;
    }

    throw new MissingTenantError();
}

/** Suffix a partir de um Request/NextRequest — para route handlers. */
export function getClientSuffixFrom(request: Request): string {
    return resolveClientSuffix(request.headers.get(CLIENT_SUFFIX_HEADER));
}

/**
 * Suffix do request atual, via next/headers. Use em Server Components e no SSR.
 * Chamar isto no browser é erro de programação — lança.
 */
export async function getClientSuffix(): Promise<string> {
    if (typeof window !== "undefined") {
        throw new Error(
            "getClientSuffix() é server-only. No browser o tenant vem do bootstrap " +
                "(useBootstrap().clientSuffix) e não deve ser usado para chamar a API."
        );
    }
    const { headers } = await import("next/headers");
    return resolveClientSuffix((await headers()).get(CLIENT_SUFFIX_HEADER));
}
